/**
 * System prompts and message builders for text operations (rewrite, polish,
 * translate, etc.), Excel analysis planning, and agentic editing (opsMessages).
 */

import { ChatMessage } from "./types";

const SYS_WRITER =
  "你是一名专业的中文写作与编辑助手，嵌入在 Office 文档中。只输出最终结果文本，不要添加解释、前言或 Markdown 代码块标记。";

export type TextOp = "rewrite" | "polish" | "expand" | "summarize" | "translate" | "continue";

const OP_INSTRUCTIONS: Record<TextOp, string> = {
  rewrite: "在保持原意的前提下改写下面的文字，使其更清晰、流畅、专业。",
  polish: "润色下面的文字：修正语法、改善措辞与节奏，尽量保留原有结构与长度。",
  expand: "在下面文字的基础上扩展内容，补充细节与例子，使其更充实。",
  summarize: "用简洁的语言总结下面的文字，提炼要点。",
  translate: "翻译下面的文字。若原文为中文则译为英文，否则译为中文，保持语气与术语准确。",
  continue: "在下面文字的基础上自然地续写，延续语气和主题。",
};

export function textOpMessages(op: TextOp, content: string, extra?: string): ChatMessage[] {
  const instruction = OP_INSTRUCTIONS[op];
  const note = extra?.trim() ? `\n\n额外要求：${extra.trim()}` : "";
  return [
    { role: "system", content: SYS_WRITER },
    { role: "user", content: `${instruction}${note}\n\n---\n${content}` },
  ];
}

export function generateMessages(prompt: string, context?: string): ChatMessage[] {
  const ctx = context?.trim() ? `\n\n参考上下文：\n${context.trim()}` : "";
  return [
    { role: "system", content: SYS_WRITER },
    { role: "user", content: `请根据以下要求生成内容：${prompt}${ctx}` },
  ];
}

export function commentMessages(content: string, focus?: string): ChatMessage[] {
  const focusNote = focus?.trim() ? `重点关注：${focus.trim()}。` : "";
  return [
    {
      role: "system",
      content:
        "你是一名审阅者。针对给定文字给出一条简短、具体、可执行的修改建议，用于作为文档批注。只输出批注内容本身，不超过 80 字。",
    },
    { role: "user", content: `${focusNote}\n\n${content}` },
  ];
}

/** Excel: ask the model to return a strict JSON analysis plan. */
export interface RegionForPlan {
  address: string;
  letters: string[];
  labels: string[];
  hasHeader: boolean;
  sampleRows: unknown[][];
}
export interface SheetForPlan {
  name: string;
  regions: RegionForPlan[];
}

export function analysisPlanMessages(sheets: SheetForPlan[], goal?: string): ChatMessage[] {
  const sheetBlocks = sheets
    .map((s, si) => {
      const regions = s.regions
        .map((r, ri) => {
          const cols = r.labels.map((label, i) => `${i}(${r.letters[i]})=${label}`).join("，");
          const hdr = r.hasHeader ? "含表头" : "无表头(列名为推断)";
          return `  区域[${ri}] 范围 ${r.address}（${hdr}）\n    列：${cols}\n    样本（最多8行）：${JSON.stringify(
            r.sampleRows.slice(0, 8)
          )}`;
        })
        .join("\n");
      return `工作表[${si}]「${s.name}」\n${regions || "  （无数据区域）"}`;
    })
    .join("\n\n");
  const schema = `{
  "summary": "对（可能多个）工作表/数据区域的整体描述与彼此关联（2-5 句）",
  "insights": ["关键发现1", "关键发现2", "..."],
  "charts": [
    { "type": "bar|column|line|pie|area|scatter", "title": "图表标题", "sheetIndex": 0, "regionIndex": 0, "columnIndexes": [0, 2] }
  ],
  "pivot": { "sheetIndex": 0, "regionIndex": 0, "rowIndexes": [0], "colIndexes": [], "valueIndexes": [2] }
}`;
  return [
    {
      role: "system",
      content:
        "你是一名数据分析师。下面给出若干工作表，每个工作表可能包含多个相互独立的数据区域（区域已自动探测，可能没有规整表头）。请进行综合分析并规划可视化与透视表。只输出一个 JSON 对象，严格匹配给定结构。\n" +
        "重要：用 sheetIndex+regionIndex 定位数据区域，用“列序号”引用列（即每个区域里 0、1、2… 的编号）。不要写列名、不要臆造或改写表头。columnIndexes/rowIndexes/colIndexes/valueIndexes 都是所选区域内的列序号（整数，从 0 开始）。\n" +
        "图表的 columnIndexes 第一个通常是类别轴（文本列），其余为数值列，至少 2 个。透视表的 valueIndexes 应为数值列。所有引用的列必须来自同一个区域。若某项不适用就给空数组或省略 pivot。不要输出任何额外文字或代码块标记。",
    },
    {
      role: "user",
      content: `${sheetBlocks}
${goal?.trim() ? `\n分析目标：${goal.trim()}` : ""}

请输出 JSON，结构如下（数字均为对应区域内的列序号）：
${schema}`,
    },
  ];
}

// ---------------------------------------------------------------------------
// Agentic editing: ask the model for a JSON list of operations to perform.
// ---------------------------------------------------------------------------

export type ActHost = "word" | "excel" | "powerpoint";

const ACT_SCHEMAS: Record<ActHost, string> = {
  word: `{
  "say": "用一句中文说明你将做什么",
  "ops": [
    // 选其中需要的若干种，按顺序执行：
    {"op": "insertParagraph", "text": "段落/标题文字", "style": "title|subtitle|heading1|heading2|heading3|normal", "alignment": "left|center|right|justify", "where": "start|end|cursor", "bold": false, "italic": false, "fontSize": 14, "color": "#000000"},
    {"op": "replaceSelection", "text": "替换选区的文字", "style": "...", "alignment": "...", "bold": true},
    {"op": "formatSelection", "bold": true, "italic": false, "underline": false, "alignment": "center", "style": "heading1", "fontSize": 14, "color": "#C00000"},
    {"op": "insertText", "text": "纯文本（不新建段落）", "where": "cursor|start|end"},
    {"op": "insertTable", "rows": [["表头1","表头2"],["a","b"]], "where": "start|end"}
  ]
}`,
  powerpoint: `{
  "say": "用一句中文说明你将做什么",
  "ops": [
    {"op": "addSlide", "title": "幻灯片标题", "bullets": ["要点1","要点2"]},
    {"op": "addSlides", "slides": [{"title": "第一页", "bullets": ["..."]}, {"title": "第二页", "bullets": ["..."]}]},
    {"op": "replaceSelection", "text": "替换选中文本框的内容"}
  ]
}`,
  excel: `{
  "say": "用一句中文说明你将做什么",
  "ops": [
    {"op": "writeRange", "sheet": "可选工作表名", "address": "起始单元格如 A1", "values": [["表头1","表头2"],["1","2"]]},
    {"op": "putFormula", "sheet": "可选", "address": "C2", "formula": "=A2*B2"},
    {"op": "setCell", "sheet": "可选", "address": "A1", "value": "文本或数字"}
  ]
}`,
};

const ACT_HINTS: Record<ActHost, string> = {
  word:
    "这是 Word 文档。标题一般用 style:\"title\" 并按需要 alignment:\"center\"，通常 where:\"start\"。需要在光标处插入则 where:\"cursor\"。",
  powerpoint: "这是 PowerPoint。新增内容用 addSlide/addSlides；修改当前选中文本框用 replaceSelection。",
  excel: "这是 Excel。写入数据用 writeRange（values 是二维数组），写公式用 putFormula。",
};

export function opsMessages(host: ActHost, instruction: string, context?: string, history?: ChatMessage[]): ChatMessage[] {
  const ctx = context && context.trim() ? `\n\n参考内容：\n${context.trim()}` : "";
  const prior = (history ?? []).filter((m) => m.role === "user" || m.role === "assistant");
  return [
    {
      role: "system",
      content:
        "你是嵌入在 Office 中的编辑助手，可以直接操作文档。请把用户的指令转换成一组结构化操作，只输出一个 JSON 对象，严格匹配给定结构；不要输出多余文字或代码块标记。" +
        "可参考前面的对话来理解“上面那段/刚才的标题”等指代。只使用满足指令所需的最少操作。无法用这些操作完成时，让 ops 为空数组，并在 say 中说明原因。" +
        ACT_HINTS[host],
    },
    ...prior,
    {
      role: "user",
      content: `指令：${instruction}${ctx}\n\n请输出 JSON，结构如下：\n${ACT_SCHEMAS[host]}`,
    },
  ];
}

// ---------------------------------------------------------------------------
// Clean-for-insert: turn a free-form chat reply into host-native content
// (NO Markdown, NO conversational filler) — used ONLY by the insert / update
// toolbar actions, never by the normal chat flow.
// ---------------------------------------------------------------------------

export type CleanHost = "word" | "excel" | "powerpoint";

const CLEAN_COMMON =
  "下面是助手在对话中给出的一段回复。请把它转换成可以直接落入 Office 文档的干净内容。" +
  "必须丢弃任何对话式寒暄、解释、前言、结语（例如「好的」「以下是」「希望对你有帮助」「请告诉我是否…」），" +
  "必须丢弃任何 Markdown 装饰（如 **、##、```、`、-、* 等标记），" +
  "也不要把字面的 \\n、\\t 当成可见字符保留——遇到它们要还原成真实的换行/制表符。" +
  "只输出最终要进入文档的内容本身；如果整段都是寒暄或解释、没有实质内容，请输出空字符串或空 JSON。";

export function cleanForInsertMessages(host: CleanHost, source: string): ChatMessage[] {
  if (host === "word") {
    return [
      {
        role: "system",
        content:
          CLEAN_COMMON +
          "\n目标是 Word 文档。请输出纯文本：每个段落独占一行，段落之间留一个空行（即段落之间用一个空行分隔）。" +
          "不要输出 JSON、不要输出代码块标记、不要保留 Markdown 标题/列表符号。",
      },
      { role: "user", content: source },
    ];
  }
  if (host === "excel") {
    return [
      {
        role: "system",
        content:
          CLEAN_COMMON +
          "\n目标是 Excel 工作表。请输出一个 JSON 对象，结构严格为 {\"paragraphs\": [\"段落1\", \"段落2\", ...]}，" +
          "数组中每个字符串是一行/一段（字符串内部不得包含换行）。只输出该 JSON，不要任何额外文字或代码块标记。",
      },
      { role: "user", content: source },
    ];
  }
  // powerpoint
  return [
    {
      role: "system",
      content:
        CLEAN_COMMON +
        "\n目标是 PowerPoint 幻灯片。请输出一个 JSON 对象，结构严格为 {\"title\": \"幻灯片标题\", \"bullets\": [\"要点1\", \"要点2\", ...]}。" +
        "title 不超过 20 字；bullets 中每条不超过 40 字、至少 1 条、最多 6 条，每条都是独立短句（字符串内部不得包含换行）。" +
        "只输出该 JSON，不要任何额外文字或代码块标记。",
    },
    { role: "user", content: source },
  ];
}
